/**
 * Portal Intel Tab — Transfer portal status and upcoming window info
 *
 * NCAA Baseball Transfer Portal Windows (2025-2026 academic year):
 *   1. Fall/Winter (all spring sports): December 1 – December 15 (15 days)
 *   2. Spring/Summer (baseball-specific): June 1 – June 30 (30 days)
 *
 * When the portal is closed, shows a status banner with countdown to
 * the next window. When open, shows the active window with days remaining.
 */
import React, { useMemo } from 'react';
import { Lock, Unlock, Calendar, Clock, AlertCircle } from 'lucide-react';

const C = {
  bg: '#0a0a0a', card: '#141414', border: '#1e1e1e',
  text: '#e5e5e5', t2: '#888', gold: '#c9a227',
  dev: '#ef4444', green: '#22c55e',
};

function getPortalStatus() {
  const now = new Date();
  const year = now.getFullYear();

  const fallOpen = new Date(year, 11, 1);    // Dec 1
  const fallClose = new Date(year, 11, 15);  // Dec 15
  const summerOpen = new Date(year, 5, 1);   // Jun 1
  const summerClose = new Date(year, 5, 30); // Jun 30

  if (now >= summerOpen && now <= summerClose) {
    const daysLeft = Math.ceil((summerClose - now) / 86_400_000);
    return { open: true, window: 'Summer', daysLeft, nextOpen: null, nextWindow: 'Fall/Winter' };
  }
  if (now >= fallOpen && now <= fallClose) {
    const daysLeft = Math.ceil((fallClose - now) / 86_400_000);
    return { open: true, window: 'Fall/Winter', daysLeft, nextOpen: null, nextWindow: 'Summer' };
  }

  let nextOpen, nextWindow, windowDates;
  if (now < summerOpen) {
    nextOpen = summerOpen;
    nextWindow = 'Summer';
    windowDates = 'June 1 – June 30';
  } else if (now > summerClose && now < fallOpen) {
    nextOpen = fallOpen;
    nextWindow = 'Fall/Winter';
    windowDates = 'December 1 – December 15';
  } else {
    nextOpen = new Date(year + 1, 5, 1);
    nextWindow = 'Summer';
    windowDates = 'June 1 – June 30';
  }

  const daysUntil = Math.ceil((nextOpen - now) / 86_400_000);
  return { open: false, window: null, daysLeft: null, nextOpen, nextWindow, windowDates, daysUntil };
}

export default function PortalIntelTab({ sport = 'baseball', embedded = false }) {
  const portalStatus = useMemo(() => getPortalStatus(), []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Portal Window Status Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', borderRadius: 14,
        background: portalStatus.open
          ? 'linear-gradient(135deg, #0a2e0a, #0a1a0a)'
          : 'linear-gradient(135deg, #2e1a0a, #1a0a0a)',
        border: `1px solid ${portalStatus.open ? '#22c55e30' : '#FF444430'}`,
      }}>
        {portalStatus.open ? (
          <Unlock size={20} style={{ color: C.green, flexShrink: 0 }} />
        ) : (
          <Lock size={20} style={{ color: C.dev, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 13, fontWeight: 700,
            color: portalStatus.open ? C.green : C.dev,
            letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            {portalStatus.open
              ? `Transfer Portal OPEN — ${portalStatus.window} Window`
              : 'Transfer Portal CLOSED'}
          </div>
          <div style={{ fontSize: 11, color: C.t2, marginTop: 3 }}>
            {portalStatus.open
              ? `${portalStatus.daysLeft} day${portalStatus.daysLeft !== 1 ? 's' : ''} remaining in the ${portalStatus.window} window`
              : `Next window: ${portalStatus.nextWindow} (${portalStatus.windowDates}) — opens in ~${portalStatus.daysUntil} days`}
          </div>
        </div>
        <Calendar size={14} style={{ color: C.t2, flexShrink: 0 }} />
      </div>

      {/* Portal Opening Soon — Main content area */}
      <div style={{
        borderRadius: 16,
        border: `1px solid ${C.border}`,
        background: C.card,
        padding: '48px 32px',
        textAlign: 'center',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14, margin: '0 auto 20px',
          background: `linear-gradient(135deg, ${C.gold}20, ${C.gold}08)`,
          border: `1px solid ${C.gold}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Clock size={24} style={{ color: C.gold }} />
        </div>

        <h3 style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 8 }}>
          {portalStatus.open ? 'Portal Intelligence Active' : 'Portal Intelligence Opening Soon'}
        </h3>

        <p style={{ fontSize: 13, color: C.t2, maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
          {portalStatus.open
            ? 'The transfer portal is currently open. Live prospect data, scouting intelligence, and SLAM Fit Scores will be available here during the active window.'
            : `The NCAA Baseball Transfer Portal ${portalStatus.nextWindow} window opens ${portalStatus.windowDates}. When the portal opens, this tab will feature live prospect tracking, AI scouting reports, and SLAM Fit Score evaluations.`}
        </p>

        {/* NCAA Windows Reference */}
        <div style={{
          marginTop: 32, padding: '16px 24px', borderRadius: 12,
          background: C.bg, border: `1px solid ${C.border}`,
          display: 'inline-block', textAlign: 'left',
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.gold, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
            NCAA Baseball Portal Windows
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.gold, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>Summer Window</div>
                <div style={{ fontSize: 11, color: C.t2 }}>June 1 – June 30 (baseball-specific, 30 days)</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.gold, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>Fall/Winter Window</div>
                <div style={{ fontSize: 11, color: C.t2 }}>December 1 – December 15 (all spring sports, 15 days)</div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: C.t2, marginTop: 10, fontStyle: 'italic' }}>
            Graduate transfers, coaching changes, and aid reductions may enter anytime.
          </div>
        </div>
      </div>
    </div>
  );
}
